







import MapNew from "@/components/Map/MapNew.vue";
import Map from "@/components/Map/Map.vue";
import { Component, Vue } from "vue-property-decorator";
import mapStore from "@/store/mapStore";
import userStore from "@/store/userStore";
import { reaction } from "mobx";
import { TruckLocationModel } from "@/api/TruckLocations";
import eventsHub from "@/hubs/EventsHub";
import { RequestEvent, BinEvent, GPSUpdatedEvent, VideoRequestEvent, CommercialGpsUpdatedEvent } from "@/hubs/events";
import { CollectionEventSummaryModel, CollectionIndexModel } from "@/api/CollectionEvents";
import { geoToH3 } from "h3-js";
import { MapProvider } from "@/api/CouncilConfiguration";

@Component({ components: { MapNew, Map } })
class Home extends Vue {
  mapStore = mapStore;
  dispose?: () => void;
  eventsHub = eventsHub;
  clusterSettings = {
    disableClusteringAtZoom: 18
  };

  async mounted() {
    eventsHub.gpsUpdatedEvent.subscribe(this.onGpsUpdatedEvent);
    eventsHub.commercialGpsUpdatedEvent.subscribe(this.onCommercialGpsUpdatedEvent);
    eventsHub.binEvent.subscribe(this.onBinEvent);
    eventsHub.requestEvent.subscribe(this.onRequestEvent);
    eventsHub.videoRequestEvent.subscribe(this.onVideoRequestEvent);

    this.fetchData();
    const disposeData = reaction(() => [mapStore.date, mapStore.activeCouncil], this.fetchData);

    this.dispose = () => {
      disposeData();
    };
  }

  async destroyed() {
    if (this.dispose) this.dispose();
    eventsHub.gpsUpdatedEvent.unsubscribe(this.onGpsUpdatedEvent);
    eventsHub.commercialGpsUpdatedEvent.unsubscribe(this.onCommercialGpsUpdatedEvent);
    eventsHub.binEvent.unsubscribe(this.onBinEvent);
    eventsHub.requestEvent.unsubscribe(this.onRequestEvent);
    eventsHub.videoRequestEvent.unsubscribe(this.onVideoRequestEvent);
  }

  private fetchData() {
    if (!mapStore.date || !mapStore.activeCouncil) return;
    mapStore.isCollectionEventPopupOpen = false;

    if (mapStore.zoom >= this.clusterSettings.disableClusteringAtZoom) {
      mapStore.fetchCollectionEvents();
    } else {
      mapStore.fetchCollectionIndexes();
    }

    mapStore.fetchTruckLocations();
    mapStore.fetchRequests();
    if (userStore.canAccessVideoRequests()) {
      mapStore.fetchVideoRequests();
    }
  }

  private onGpsUpdatedEvent(payload: GPSUpdatedEvent) {
    const truckLocation: TruckLocationModel = {
      id: 1,
      truckCode: payload.truckCode,
      siteNumber: payload.siteNumber,
      council: payload.council,
      wasteStreamId: payload.wasteStreamId,
      latitude: payload.latitude,
      longitude: payload.longitude,
      heading: payload.heading,
      eventDateTime: payload.eventDateTime,
      truckStatus: "InRun",
      runNumber: payload.runNumber
    };
    mapStore.updateTruckLocation(truckLocation);
  }

  private onCommercialGpsUpdatedEvent(payload: CommercialGpsUpdatedEvent) {
    const truckLocation: TruckLocationModel = {
      id: 1,
      truckCode: payload.truckCode,
      siteNumber: payload.siteNumber,
      council: payload.council,
      wasteStreamId: payload.wasteStreamId,
      latitude: payload.latitude,
      longitude: payload.longitude,
      heading: payload.heading,
      eventDateTime: payload.eventDateTime,
      truckStatus: payload.truckStatus,
      runNumber: payload.runNumber,
      isCommercial: true
    };
    mapStore.updateTruckLocation(truckLocation);
  }

  private onRequestEvent(payload: RequestEvent) {
    if (mapStore.isToday) {
      const index = mapStore.requests.findIndex(r => r.requestId === payload.requestId);
      if (index !== -1) {
        mapStore.requests.splice(index, 1);
      }
      mapStore.requests.push(payload);
    }
  }

  private onVideoRequestEvent(payload: VideoRequestEvent) {
    // TODO: Check if date matches current filter ... if (payload.startAt)
    const index = mapStore.videoRequests.findIndex(r => r.id === payload.id);
    if (index !== -1) {
      mapStore.videoRequests.splice(index, 1);
    }
    mapStore.videoRequests.push(payload);
  }

  private onBinEvent(payload: BinEvent) {
    if (!this.map?.markerLayer) {
      return;
    }

    if (!mapStore.isToday) {
      return;
    }

    if (mapStore.zoom < this.clusterSettings.disableClusteringAtZoom) {
      const isInBounds = mapStore.isInBounds(payload.latitude, payload.longitude);
      if (!isInBounds) {
        console.debug(`Ignoring BinEvent[${payload.id}] is not in bounds`);
        return;
      }

      const index = geoToH3(payload.latitude, payload.longitude, mapStore.resolution);
      const existingModel = mapStore.collectionIndexes.find(x => x.index === index);

      if (existingModel) {
        console.debug(`Collection Index[${index}] found updating collection index`);
        const wasteStream = existingModel.wasteStreams.find(x => x.wasteStreamId == payload.wasteStreamId);
        if (wasteStream) {
          if (payload.isCollectionIssue === 1) {
            ++wasteStream.collectionIssues;
          } else {
            ++wasteStream.collections;
          }
        } else {
          existingModel.wasteStreams.push({
            wasteStreamId: payload.wasteStreamId,
            collections: payload.isCollectionIssue === 1 ? 0 : 1,
            collectionIssues: payload.isCollectionIssue === 1 ? 1 : 0
          });
        }
        const oldMarkerIndex = this.map.indexMarkers.findIndex(x => x.index == index);

        if (mapStore.isHereVectorMap) {
          mapStore.collectionIndexes = [...mapStore.collectionIndexes.slice(0, oldMarkerIndex), existingModel, ...mapStore.collectionIndexes.slice(oldMarkerIndex + 1)];
        } else {
          if (oldMarkerIndex >= 0) {
            this.map.markerLayer.removeLayer(this.map.indexMarkers[oldMarkerIndex].marker);
            this.map.indexMarkers.splice(oldMarkerIndex, 2);
          }

          const marker = this.map.createCollectionIndexMarker(existingModel);
          if (marker) {
            this.map.markerLayer.addLayer(marker);
            this.map.indexMarkers.push({ index, marker });
          }
        }
      } else {
        console.debug(`Collection Index[${index}] not found creating collection index`);
        const model: CollectionIndexModel = {
          index,
          latitude: payload.latitude,
          longitude: payload.longitude,
          wasteStreams: [
            {
              wasteStreamId: payload.wasteStreamId,
              collections: payload.isCollectionIssue === 1 ? 0 : 1,
              collectionIssues: payload.isCollectionIssue === 1 ? 1 : 0
            }
          ]
        };

        mapStore.collectionIndexes.push(model);
        //in new vector map we don't need to create marker here
        if (!mapStore.isHereVectorMap) {
          const marker = this.map.createCollectionIndexMarker(model);
          if (marker) {
            this.map.markerLayer.addLayer(marker);
            this.map.indexMarkers.push({ index, marker });
          }
        }
      }
    } else {
      const model: CollectionEventSummaryModel = {
        id: payload.id,
        latitude: payload.latitude,
        longitude: payload.longitude,
        wasteStreamId: payload.wasteStreamId,
        isCollectionIssue: payload.isCollectionIssue === 1,
        collectionEventType: payload.collectionEventType,
        addressHash: payload.addressHash
      };
      mapStore.previuosCollectionEvents = mapStore.collectionEvents;
      mapStore.collectionEvents.push(model);
    }
  }

  get map(): MapNew | Map {
    return userStore.councilConfiguration?.mapProvider == MapProvider.HereVector ? (this.$refs.mapNew as MapNew) : (this.$refs.map as Map);
  }
}

export default Home;
